import { useState, useEffect, useRef } from "react";
import "../styles/Termsofservice.css";
import MainFooter from "../components/MainFooter";

const sections = [
  {
    id: "introduction",
    number: "1.",
    title: "Introduction & Agreement",
    content: [
      `Welcome to Eventfork.com ("Eventfork", "we", "us", or "our"), a comprehensive event management platform that enables individuals and organisations to create, manage, and promote events including conferences, meetups, expos, workshops, and other professional gatherings.`,
      `These Terms of Service ("Terms") constitute a legally binding agreement between you ("User", "you", or "your") and Eventfork Technologies Pvt. Ltd. governing your access to and use of the Eventfork.com platform, including all associated features, tools, APIs, and services.`,
      `By registering an account, creating an event, purchasing a ticket, or otherwise accessing our platform, you confirm that you have read, understood, and agree to be bound by these Terms and our Privacy Policy. If you do not agree, you must immediately discontinue use of the platform.`,
    ],
  },
  {
    id: "definitions",
    number: "2.",
    title: "Definitions",
    content: [`For the purposes of these Terms, the following definitions apply:`],
    table: {
      headers: ["Term", "Definition"],
      rows: [
        ["Platform", "The Eventfork.com website, mobile applications, APIs, and all associated digital services."],
        ["Event Organiser", "Any individual or entity that creates, publishes, or manages an event on the Platform."],
        ["Attendee", "Any individual who registers for, purchases a ticket to, or participates in an event listed on the Platform."],
        ["Speaker", "Any individual listed as a presenter, panellist, or keynote for an event managed through the Platform."],
        ["Sponsor / Exhibitor", "Any individual or entity that participates in an event as a sponsor, exhibitor, or vendor at expos or conferences."],
        ["Event", "Any conference, meetup, expo, workshop, webinar, hackathon, or other gathering created and managed via the Platform."],
        ["Content", "Any information, text, images, videos, schedules, speaker bios, session descriptions, or other materials submitted to the Platform."],
        ["Ticket", "A digital pass or registration confirmation granting an Attendee access to a specific Event."],
        ["Organiser Dashboard", "The administrative interface provided to Event Organisers for creating and managing events, attendees, speakers, sponsors, and analytics."],
      ],
    },
  },
  {
    id: "eligibility",
    number: "3.",
    title: "Eligibility & Account Registration",
    content: [
      `To use the Eventfork platform, you must be at least 18 years of age and have the legal capacity to enter into a binding contract. By using the platform, you represent and warrant that you meet these requirements.`,
    ],
    bullets: [
      { label: "Account Creation", text: "You must register an account to access most features. You agree to provide accurate, current, and complete information during registration and to keep your account details up to date." },
      { label: "Account Security", text: "You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. Notify us immediately at info@eventfork.com of any suspected unauthorised access." },
      { label: "Organisational Accounts", text: "If you register on behalf of an organisation, company, or institution, you warrant that you have the authority to bind that entity to these Terms." },
      { label: "Account Suspension", text: "We reserve the right to suspend or terminate accounts that provide false information, violate these Terms, or engage in conduct that harms the platform or other users." },
    ],
  },
  {
    id: "event-creation",
    number: "4.",
    title: "Event Creation & Management",
    content: [
      `Eventfork provides Event Organisers with a comprehensive suite of tools to create and manage events of all types — from intimate meetups to large-scale conferences, expos, and multi-track summits.`,
    ],
    bullets: [
      { label: "Event Listing", text: "Organisers may create public or private event listings including event name, description, date, venue or virtual link, agenda, speaker line-up, ticket types, and sponsor information." },
      { label: "Event Types", text: "The platform supports conferences (multi-track, multi-day), meetups, expos and trade shows, workshops, hackathons, webinars, hybrid events, and private corporate gatherings." },
      { label: "Accuracy of Information", text: "Organisers are solely responsible for the accuracy, completeness, and legality of all event information published on the Platform. Misleading or false event listings are prohibited and may result in immediate removal." },
      { label: "Event Modifications", text: "Organisers may modify event details at any time prior to the event. Material changes (date, venue, cancellation) must be communicated to all registered Attendees promptly through the Platform's notification tools." },
      { label: "Event Cancellation", text: "If an Organiser cancels an event, they must initiate refunds to all paid Attendees within 7 business days. Failure to do so may result in platform-initiated refunds charged to the Organiser." },
      { label: "Prohibited Events", text: "Events that promote illegal activities, hate speech, discrimination, violence, or that violate any applicable law are strictly prohibited and will be removed without notice." },
    ],
  },
  {
    id: "attendees",
    number: "5.",
    title: "Attendee Registration & Tickets",
    content: [
      `Attendees may register for events listed on Eventfork.com, subject to availability and any eligibility criteria set by the Event Organiser.`,
    ],
    bullets: [
      { label: "Registration", text: "Attendees must provide accurate registration information. Some events may require additional fields such as dietary requirements, accessibility needs, company affiliation, or professional credentials." },
      { label: "Ticket Transfers", text: "Unless otherwise stated by the Organiser, tickets may be transferred to another individual prior to the event. Transfers must be completed through the Platform's transfer tool." },
      { label: "Refund Policy", text: "Refund eligibility is governed by the individual Organiser's refund policy as set at the time of registration. Eventfork's platform fee (where applicable) is non-refundable except where required by law." },
      { label: "Event Admission", text: "Eventfork is not responsible for an Organiser's decision to refuse admission. Attendees should review event terms set by the Organiser before registering." },
      { label: "Check-In & Badges", text: "Attendees may use the Eventfork mobile app or QR code for on-site check-in and digital badge generation at supported events." },
      { label: "Virtual Events", text: "For online or hybrid events, Attendees are responsible for ensuring they have the necessary device, internet connection, and software to participate. Access links are personal and must not be shared." },
    ],
  },
  {
    id: "speakers",
    number: "6.",
    title: "Speaker Management",
    content: [
      `Eventfork provides dedicated tools for Organisers to manage speakers, including call-for-papers workflows, speaker profiles, session scheduling, and communication.`,
    ],
    bullets: [
      { label: "Speaker Profiles", text: "Organisers may invite speakers to create profiles on the Platform. Speaker profiles include biography, headshot, social links, and session descriptions visible to Attendees." },
      { label: "Call for Papers (CFP)", text: "Organisers may publish a CFP to invite session proposals. Accepted speakers will be notified via the Platform and their sessions will appear in the public event agenda." },
      { label: "Speaker Consent", text: "By creating a speaker profile or accepting a speaking invitation, Speakers consent to their name, biography, photograph, and session information being displayed publicly on the event page." },
      { label: "Session Content", text: "Speakers are solely responsible for the content of their presentations. Content that is defamatory, infringing, or unlawful is prohibited. Organisers have the right to remove or modify sessions at their discretion." },
      { label: "Recording & Livestream", text: "Organisers must clearly indicate if sessions will be recorded or streamed. Speakers must be informed and provide consent prior to recording. Eventfork is not responsible for disputes between Organisers and Speakers regarding recordings." },
      { label: "Speaker Compensation", text: "Any compensation, travel arrangements, or honoraria between Organisers and Speakers are governed solely by their bilateral agreement. Eventfork is not a party to such arrangements." },
    ],
  },
  {
    id: "sponsors-exhibitors",
    number: "7.",
    title: "Sponsors & Exhibitors",
    content: [
      `Eventfork enables Organisers to manage sponsor tiers, exhibitor booths, and partnership listings for expos and conferences. The following terms apply to sponsor and exhibitor participation:`,
    ],
    bullets: [
      { label: "Sponsor Listings", text: "Organisers may create sponsorship tiers and list sponsor logos, descriptions, and links on the event page. Sponsors grant the Organiser a licence to display their brand assets for event promotion." },
      { label: "Expo & Booth Management", text: "For expo-style events, Organisers may assign virtual or physical booth spaces to exhibitors, manage floor plans, and provide exhibitors with a dedicated management portal." },
      { label: "Lead Retrieval", text: "Exhibitors at supported events may access lead retrieval tools to scan Attendee badges (subject to Attendee consent settings as configured by the Organiser)." },
      { label: "Sponsor Conduct", text: "Sponsors and exhibitors must comply with all event rules set by the Organiser and with Eventfork's platform policies. Disruptive, misleading, or inappropriate conduct may result in removal from the event." },
      { label: "Financial Arrangements", text: "Sponsorship fees and exhibitor booth charges are arrangements solely between Organisers and Sponsors. Eventfork is not a party to sponsorship agreements and bears no liability for their fulfilment." },
    ],
  },
  {
    id: "payments",
    number: "8.",
    title: "Payments, Fees & Billing",
    content: [
      `Eventfork facilitates payment processing for paid events. All financial transactions on the Platform are subject to the following terms:`,
    ],
    bullets: [
      { label: "Platform Fees", text: "Eventfork charges a platform service fee on paid ticket transactions. The applicable fee structure is displayed in the Organiser Dashboard at the time of event creation and is subject to change with 30 days' notice." },
      { label: "Payment Processing", text: "Payments are processed by authorised third-party payment gateways. By purchasing a ticket, you agree to the terms of the relevant payment processor. Eventfork does not store full payment card details." },
      { label: "Currency", text: "All transactions are processed in the currency selected by the Event Organiser. Attendees may incur currency conversion fees from their bank or card provider." },
      { label: "Payouts to Organisers", text: "Collected ticket revenues (less platform fees and applicable taxes) are disbursed to Organisers according to the payout schedule agreed at registration. Payouts may be withheld in cases of dispute, fraud investigation, or policy violations." },
      { label: "Taxes", text: "Organisers are responsible for determining and remitting applicable taxes (including GST, VAT, or service tax) on ticket sales. Eventfork may collect and remit taxes where required by law." },
      { label: "Chargebacks", text: "In the event of a chargeback, Eventfork reserves the right to deduct the chargeback amount and any associated fees from the Organiser's payout balance." },
    ],
  },
  {
    id: "content",
    number: "9.",
    title: "User Content & Intellectual Property",
    content: [
      `Users may submit various types of content to the Platform including event descriptions, speaker bios, session materials, images, videos, and communications. The following terms govern User Content:`,
    ],
    bullets: [
      { label: "Ownership", text: "You retain ownership of all Content you submit to the Platform. By submitting Content, you grant Eventfork a non-exclusive, worldwide, royalty-free licence to host, display, and distribute your Content solely for the purpose of operating and promoting the Platform and your events." },
      { label: "Content Standards", text: "All Content must be accurate, lawful, and not infringe any third-party intellectual property rights. Content that is defamatory, obscene, discriminatory, or misleading is prohibited." },
      { label: "Eventfork IP", text: "The Eventfork platform, brand, logo, software, and all associated intellectual property are owned by Eventfork Technologies Pvt. Ltd. You may not reproduce, modify, or distribute our intellectual property without prior written consent." },
      { label: "Content Removal", text: "We reserve the right to remove any Content that violates these Terms or our policies, without prior notice and without liability to you." },
      { label: "DMCA / Takedown", text: "If you believe any Content on the Platform infringes your intellectual property rights, please submit a formal notice to info@eventfork.com with details of the claimed infringement." },
    ],
  },
  {
    id: "prohibited",
    number: "10.",
    title: "Prohibited Conduct",
    content: [
      `The following activities are strictly prohibited on the Eventfork platform. Violations may result in immediate account suspension, termination, and/or legal action:`,
    ],
    bullets: [
      { label: "Fraudulent Activity", text: "Creating fake events, selling counterfeit tickets, impersonating other users or organisations, or engaging in any form of fraudulent misrepresentation." },
      { label: "Spam & Harassment", text: "Sending unsolicited communications to Attendees or other users, harassing speakers or organisers, or using the Platform's messaging tools for bulk spam." },
      { label: "Data Scraping", text: "Using automated tools, bots, or scripts to scrape, harvest, or extract data from the Platform without prior written authorisation." },
      { label: "Security Violations", text: "Attempting to circumvent platform security measures, access other users' accounts, or introduce malicious code to the Platform." },
      { label: "Unauthorised Resale", text: "Reselling tickets above face value (scalping) unless explicitly permitted by the Event Organiser and in compliance with applicable laws." },
      { label: "Policy Evasion", text: "Creating multiple accounts to circumvent suspensions or bans, or using the Platform in a manner intended to evade these Terms." },
    ],
  },
  {
    id: "data",
    number: "11.",
    title: "Data, Analytics & Attendee Privacy",
    content: [
      `Eventfork collects and processes personal data as described in our Privacy Policy. In the context of event management, the following additional terms apply to Organiser access to Attendee data:`,
    ],
    bullets: [
      { label: "Organiser Data Access", text: "Organisers may access Attendee registration data (name, email, registration answers) for the sole purpose of managing event participation. This data must not be used for unrelated marketing or shared with unauthorised third parties." },
      { label: "Attendee Consent", text: "By registering for an event, Attendees consent to sharing their registration information with the Event Organiser. Organisers must honour Attendee data requests including access, correction, and deletion." },
      { label: "Data Processing Agreement", text: "Organisers who collect personal data through the Platform act as independent data controllers. Enterprise Organisers may request a Data Processing Agreement (DPA) by contacting info@eventfork.com." },
      { label: "Analytics", text: "Eventfork provides Organisers with anonymised and aggregated event analytics including registration trends, attendance rates, and engagement metrics. Individual Attendee data is only accessible in identifiable form through the Organiser Dashboard." },
    ],
    footer: `Eventfork's full data practices are set out in the Privacy Policy available at eventfork.com/privacy.`,
  },
  {
    id: "liability",
    number: "12.",
    title: "Disclaimer & Limitation of Liability",
    content: [
      `The Eventfork platform is provided on an "as is" and "as available" basis. To the maximum extent permitted by applicable law, Eventfork disclaims all warranties, express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, and non-infringement.`,
      `Eventfork acts solely as a technology platform facilitating connections between Event Organisers and Attendees. We are not responsible for the actions, omissions, content, or conduct of any Event Organiser, Attendee, Speaker, or third party.`,
    ],
    bullets: [
      { label: "Event Quality", text: "Eventfork makes no warranties regarding the quality, safety, accuracy, or suitability of any event listed on the Platform. Attendees engage with events at their own risk." },
      { label: "Service Availability", text: "While we strive for maximum uptime, Eventfork does not guarantee uninterrupted access to the Platform. Scheduled maintenance will be communicated in advance where practicable." },
      { label: "Liability Cap", text: "To the maximum extent permitted by law, Eventfork's total liability to you for any claims arising under these Terms shall not exceed the amount you paid to Eventfork in the 3 months preceding the claim." },
      { label: "Consequential Losses", text: "Eventfork shall not be liable for any indirect, incidental, special, or consequential damages including loss of profits, revenue, data, or goodwill." },
    ],
  },
  {
    id: "indemnity",
    number: "13.",
    title: "Indemnification",
    content: [
      `You agree to indemnify, defend, and hold harmless Eventfork Technologies Pvt. Ltd., its affiliates, officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, costs, and expenses (including reasonable legal fees) arising out of or related to:`,
    ],
    bullets: [
      { label: "Terms Violation", text: "Your breach of any provision of these Terms or any applicable law or regulation." },
      { label: "Content Liability", text: "Any Content you submit to the Platform that infringes third-party rights or causes harm to any person." },
      { label: "Event Liability", text: "Any event you organise through the Platform, including any claims by Attendees, Speakers, or third parties arising from that event." },
      { label: "Misrepresentation", text: "Any false or misleading representations made by you in connection with your use of the Platform." },
    ],
  },
  {
    id: "termination",
    number: "14.",
    title: "Termination & Account Closure",
    content: [
      `Either party may terminate their relationship under these Terms at any time, subject to the following provisions:`,
    ],
    bullets: [
      { label: "User Termination", text: "You may close your account at any time by contacting info@eventfork.com. Closing your account does not cancel any active events or affect obligations to Attendees." },
      { label: "Eventfork Termination", text: "We may suspend or permanently terminate your account immediately if you breach these Terms, engage in fraudulent activity, or if we are required to do so by law." },
      { label: "Effect of Termination", text: "Upon termination, your licence to use the Platform ceases. All outstanding financial obligations (to Attendees or to Eventfork) survive termination. We may retain certain data as required by law." },
      { label: "Event Continuity", text: "Where an Organiser's account is terminated with active upcoming events, Eventfork will make reasonable efforts to notify registered Attendees and facilitate refunds." },
    ],
  },
  {
    id: "governing-law",
    number: "15.",
    title: "Governing Law & Dispute Resolution",
    content: [
      `These Terms shall be governed by and construed in accordance with the laws of India. Any disputes arising in connection with these Terms shall be subject to the exclusive jurisdiction of the courts located in Pune, Maharashtra, India.`,
      `Before initiating formal legal proceedings, we encourage parties to seek resolution through our formal dispute resolution process. Please raise disputes by contacting info@eventfork.com with full details of your complaint. We will endeavour to respond within 10 business days.`,
    ],
  },
  {
    id: "amendments",
    number: "16.",
    title: "Amendments to These Terms",
    content: [
      `Eventfork reserves the right to modify these Terms of Service at any time. Where changes are material, we will provide at least 14 days' notice by email or by prominent notice on the Platform prior to the changes taking effect.`,
      `Your continued use of the Platform after the effective date of any amendment constitutes your acceptance of the revised Terms. If you do not agree to the updated Terms, you must cease using the Platform and may close your account.`,
    ],
  },
  {
    id: "contact",
    number: "17.",
    title: "Contact Information",
    content: [
      `For queries, concerns, or formal notices relating to these Terms of Service, please contact us at:`,
    ],
    contactBlock: {
      company: "Eventfork Technologies Pvt. Ltd.",
      address: "Pune, Maharashtra, India",
      general: "info@eventfork.com",
      legal: "info@eventfork.com",
      dpo: "info@eventfork.com",
    },
    footer: `We aim to acknowledge all formal correspondence within 3 business days and provide a substantive response within 10 business days.`,
  },
];

/* ─── Main component ──────────────────────────────────────────── */
export default function TermsOfService() {
  const [activeId, setActiveId]   = useState("introduction");
  const [scrollPct, setScrollPct] = useState(0);
  const [tocOpen, setTocOpen]     = useState(true);
  const sectionRefs               = useRef({});

  /* Scroll tracking + active section highlighting */
  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement;
      setScrollPct(
        (window.scrollY / (doc.scrollHeight - doc.clientHeight)) * 100
      );
      let current = sections[0].id;
      for (const s of sections) {
        const el = sectionRefs.current[s.id];
        if (el && el.getBoundingClientRect().top <= 100) current = s.id;
      }
      setActiveId(current);
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    const el = sectionRefs.current[id];
    if (el) window.scrollTo({ top: el.offsetTop - 70, behavior: "smooth" });
  };

  return (
    <div>
      {/* ── Scroll progress ── */}
      <div
        className="tos-progress"
        style={{ width: `${scrollPct}%` }}
        aria-hidden="true"
      />

      {/* ── Hero header ── */}
      <header className="tos-header">
        <div className="tos-header__orb-1" aria-hidden="true" />
        <div className="tos-header__orb-2" aria-hidden="true" />
        <div className="tos-header__inner">
          <div className="tos-header__tags">
            {["Legal Document", "Terms of Service", "Event Platform"].map((t) => (
              <span key={t} className="tag-chip tag-chip--ghost">{t}</span>
            ))}
          </div>

          <h1 className="tos-header__title">Terms of Service</h1>

          <p className="tos-header__subtitle">
            These Terms govern your use of Eventfork.com — a platform for creating
            and managing events including conferences, meetups, expos, workshops,
            and more. Please read these Terms carefully before using our services.
          </p>

          {/* <div className="tos-header__meta">
            {[
              ["📅 Effective Date", "1 January 2026"],
              ["🔄 Last Updated",   "March 2026"     ],
              ["📄 Version",        "3.0"             ],
            ].map(([label, val]) => (
              <div key={label} className="tos-header__meta-item">
                <p className="tos-header__meta-label">{label}</p>
                <p className="tos-header__meta-value">{val}</p>
              </div>
            ))}
          </div> */}
        </div>
      </header>

      {/* ── Breadcrumb ── */}
      <nav className="tos-breadcrumb" aria-label="Breadcrumb">
        <div className="tos-breadcrumb__inner">
          <span className="tos-breadcrumb__link">Home</span>
          <span aria-hidden="true">›</span>
          <span className="tos-breadcrumb__link">Legal</span>
          <span aria-hidden="true">›</span>
          <span>Terms of Service</span>
        </div>
      </nav>

      {/* ── Body ── */}
      <div className="tos-body">

        {/* ── Sidebar ── */}
        <aside className="tos-sidebar" aria-label="Document navigation">

          {/* Table of contents */}
          <div className="tos-sidebar__card">
            <button
              className="tos-toc-toggle"
              onClick={() => setTocOpen((o) => !o)}
              aria-expanded={tocOpen}
              aria-controls="tos-toc-nav"
            >
              <span className="tos-toc-toggle__label">Table of Contents</span>
              <span className="tos-toc-toggle__arrow" aria-hidden="true">
                {tocOpen ? "▲" : "▼"}
              </span>
            </button>

            {tocOpen && (
              <nav id="tos-toc-nav" className="tos-toc-nav">
                {sections.map((s) => (
                  <button
                    key={s.id}
                    className={`nav-link${activeId === s.id ? " active" : ""}`}
                    onClick={() => scrollTo(s.id)}
                    aria-current={activeId === s.id ? "location" : undefined}
                  >
                    <span className="nav-num">{s.number}</span>
                    <span>{s.title}</span>
                  </button>
                ))}
              </nav>
            )}
          </div>

          {/* Related policies */}
          {/* <div className="tos-sidebar__card">
            <div className="tos-sidebar__card-header">
              <p>Related Policies</p>
            </div>
            {[
              ["🔒", "Privacy Policy", "/privacy" ],
              ["🍪", "Cookie Policy",  "/cookies"  ],
              ["💳", "Refund Policy",  "/refunds"  ],
              ["⚖",  "DMCA Policy",   "/dmca"     ],
            ].map(([icon, label, href]) => (
              <a key={label} href={href} className="tos-related-link">
                <span aria-hidden="true">{icon}</span>
                <span>{label}</span>
              </a>
            ))}
          </div> */}

        </aside>

        {/* ── Main ── */}
        <main className="tos-main">

          {/* Warning banner */}
          <div className="tos-notice" role="note">
            <span className="tos-notice__icon" aria-hidden="true">⚠️</span>
            <p className="tos-notice__text">
              <strong>Important:</strong> These Terms of Service were last updated
              on <strong>1 January 2026</strong>. Your continued use of
              Eventfork.com after any amendments constitutes acceptance of the
              revised Terms. For questions, contact{" "}
              <a href="mailto:info@eventfork.com">info@eventfork.com</a>.
            </p>
          </div>

        

          {/* Section cards */}
          {sections.map((section, idx) => (
            <div
              key={section.id}
              id={section.id}
              ref={(el) => (sectionRefs.current[section.id] = el)}
              className="section-card"
              style={{ animationDelay: `${idx * 0.025}s` }}
            >
              <div className="sec-header">
                <span className="sec-num">{section.number}</span>
                <h2 className="sec-title">{section.title}</h2>
              </div>

              {section.content &&
                section.content.map((p, i) => (
                  <p key={i} className="prose">{p}</p>
                ))}

              {section.table && (
                <div className="tos-table-wrap">
                  <table className="data-table">
                    <thead>
                      <tr>
                        {section.table.headers.map((h) => (
                          <th key={h}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {section.table.rows.map((row, ri) => (
                        <tr key={ri}>
                          {row.map((cell, ci) => (
                            <td key={ci}>{cell}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {section.bullets && (
                <div className="bullet-grid">
                  {section.bullets.map((b, i) => (
                    <div key={i} className="bullet-row">
                      <div className="bullet-label-cell">{b.label}</div>
                      <div className="bullet-text-cell">{b.text}</div>
                    </div>
                  ))}
                </div>
              )}

              {section.contactBlock && (
                <div className="contact-box">
                  {[
                    ["Company",               section.contactBlock.company,  false],
                    ["Registered Address",    section.contactBlock.address,  false],
                    ["General Support",       section.contactBlock.general,  true ],
                    ["Legal & Compliance",    section.contactBlock.legal,    true ],
                    ["Data Protection Officer", section.contactBlock.dpo,   true ],
                  ].map(([label, value, isEmail]) => (
                    <div key={label} className="contact-row">
                      <span className="contact-label">{label}</span>
                      <span className="contact-value">
                        {isEmail
                          ? <a href={`mailto:${value}`}>{value}</a>
                          : value}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {section.footer && (
                <p className="footer-note">{section.footer}</p>
              )}
            </div>
          ))}

          {/* Bottom meta bar */}
          {/* <div className="tos-meta-bar">
            <p>
              <strong>Document:</strong> Terms of Service &nbsp;|&nbsp;{" "}
              <strong>Version:</strong> 3.0 &nbsp;|&nbsp;{" "}
              <strong>Effective:</strong> 1 Jan 2026 &nbsp;|&nbsp;{" "}
              <strong>Next Review:</strong> Jan 2026
            </p>
            <p>© 2026 Eventfork Technologies Pvt. Ltd.</p>
          </div> */}
        </main>
      </div>
      <MainFooter />
    </div>
  );
}
